import {
  UpdateCoupleProfileRequest,
  UpdatePreferencesRequest,
  SetBudgetTargetsRequest,
  ExportDataRequest,
  SettingsResponse,
  CoupleProfile,
  Preferences,
  BudgetTarget,
  ExportedData,
  AccountStatistics
} from '../../types/settings';

// API base URL for Netlify functions
const API_BASE = '/.netlify/functions';

// Update couple profile
export async function updateCoupleProfile(
  request: UpdateCoupleProfileRequest,
  accessToken: string
): Promise<SettingsResponse<CoupleProfile>> {
  try {
    const response = await fetch(`${API_BASE}/updateCoupleProfile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(request)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP error! status: ${response.status}`
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update couple profile'
    };
  }
}

// Update preferences
export async function updatePreferences(
  request: UpdatePreferencesRequest,
  accessToken: string
): Promise<SettingsResponse<Preferences>> {
  try {
    const response = await fetch(`${API_BASE}/updatePreferences`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(request)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP error! status: ${response.status}`
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update preferences'
    };
  }
}

// Set budget targets
export async function setBudgetTargets(
  request: SetBudgetTargetsRequest,
  accessToken: string
): Promise<SettingsResponse<BudgetTarget[]>> {
  try {
    const response = await fetch(`${API_BASE}/setBudgetTargets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(request)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP error! status: ${response.status}`
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set budget targets'
    };
  }
}

// Export data
export async function exportExpenseData(
  request: ExportDataRequest,
  accessToken: string
): Promise<SettingsResponse<ExportedData>> {
  try {
    const response = await fetch(`${API_BASE}/exportExpenseData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(request)
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP error! status: ${response.status}`
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to export data'
    };
  }
}

// Get account statistics (used for data export section)
export async function getAccountStatistics(
  coupleId: string,
  accessToken: string
): Promise<SettingsResponse<AccountStatistics>> {
  try {
    const response = await fetch(`${API_BASE}/getAccountStatistics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({ couple_id: coupleId })
    });

    const result = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: result.error || `HTTP error! status: ${response.status}`
      };
    }

    return result;
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to get account statistics'
    };
  }
}

// Helper function to download exported data
export function downloadExportedFile(exportedData: ExportedData): void {
  const link = document.createElement('a');
  link.href = exportedData.download_url;
  link.download = exportedData.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}